import type { FunctionComponent, ReactNode } from "react";
import { useMemo } from "react";
import { t } from "i18next";
import { Text } from "@smartsuite/react-ui/lib";
import type { ActionGroupStatus, Job } from "@smartsuite/automation-engine-api";
import { observer } from "mobx-react-lite";
import { ActivityDetailsGroup } from "../ActivityDetailsGroup/ActivityDetailsGroup";
import { BranchCard } from "./BranchCard";
import { getBranchesBlockView } from "./utils";
import { StyledStaticBox } from "./styles";

export type RenderNestedGroups = (
  groups: ActionGroupStatus[],
  recordIndex: number,
  /**
   * True when `groups` execute inside a loop iteration (their actions' record
   * jobs live at parent-record-index paths). Omitted -> the renderer keeps
   * its current scope, so branches nested in a loop inherit the loop's flag.
   */
  insideLoop?: boolean,
) => ReactNode;

interface BranchesGroupProps {
  statusGroup: ActionGroupStatus;
  job: Job;
  recordIndex?: number;
  renderNestedGroups: RenderNestedGroups;
}

export const BranchesGroup: FunctionComponent<BranchesGroupProps> = observer(
  ({ statusGroup, job, recordIndex = 0, renderNestedGroups }) => {
    // Both deps are stable references from the store, so the config walk and
    // the branch join run once per job instead of on every re-render (and
    // BranchCard's memoized props keep their identity).
    const view = useMemo(
      () =>
        getBranchesBlockView(statusGroup, job.automation?.actionGroups ?? []),
      [statusGroup, job.automation?.actionGroups],
    );
    if (view === undefined) {
      return null;
    }

    return (
      <ActivityDetailsGroup
        label={t("job-list.conditional-logic-block", { name: view.label })}
      >
        {view.noneMatched ? (
          <StyledStaticBox>
            <Text size="m">{t("job-list.no-branch-conditions-met")}</Text>
          </StyledStaticBox>
        ) : (
          view.branches.map((branch) => (
            <BranchCard
              key={branch.key}
              branch={branch}
              job={job}
              recordIndex={recordIndex}
              renderNestedGroups={renderNestedGroups}
              blockInstanceId={view.instanceId}
            />
          ))
        )}
      </ActivityDetailsGroup>
    );
  },
);
